// src/hooks/useArchivedView.js — the Active/Archived toggle, once.
//
// P0.31 task 31.B.5. Six entities soft-delete and until this phase nothing could
// read the archived rows back: the row survived and the user did not. Measured in
// production on 24 Oct 2026, archived and reachable from no screen at all — 380
// leads, 125 of 232 tasks, 89 of 133 opportunities, 24 contacts, 7 accounts. For
// tasks and opportunities that is MORE than half the table.
//
// WHY A HOOK AND NOT FOUR COPIES. SignalsPage and LeadsPage each grew their own
// version of this, and the second one already carried a bug the first did not:
// `showArchived` left out of the fetch callback's dependency array, so toggling
// the view refetched the same list and nothing changed on screen. That reads as a
// broken button rather than a stale closure. One hook means one dependency array.
//
// It deliberately does NOT fetch. Every page's loader has its own shape — some
// fetch accounts alongside, some paginate, contacts has a search mode — so the
// hook owns the STATE and the page owns the request. `param` is what the page
// appends to its URL, and `refetchKey` is what it puts in its dependency array.

import { useState, useCallback, useMemo } from 'react';

export default function useArchivedView() {
  const [showArchived, setShowArchived] = useState(false);

  const toggle = useCallback(() => setShowArchived(v => !v), []);

  // '&archived=1' or '', ready to concatenate. Returned rather than a boolean so
  // a page cannot spell the parameter differently from its siblings — which is
  // how `status=Archived` and `archived=1` came to coexist.
  const param = showArchived ? '&archived=1' : '';

  // The label and title for the toggle button, so the copy matches everywhere.
  const button = useMemo(() => ({
    label: showArchived ? '← Active' : 'Archived',
    title: showArchived ? 'Back to the active list' : 'Show archived items',
  }), [showArchived]);

  return { showArchived, setShowArchived, toggle, param, button, refetchKey: showArchived };
}
